import ADIColors from "@NA/Utils/ADIColors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import { FormFieldStyle } from "@insite/mobius/FormField";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    isAddSerialNumbersModalOpen: boolean;
    onSerialNumberModalClose: () => void;
    serialNumbersFields: [];
    handleSerialNumberChange: (data: any) => void;
    serialNumberData?: any;
    inputField?: any;
}

type Props = OwnProps;

export interface ADIAddSerialNumbersModalStyles {
    adiTextFieldGridItem?: GridItemProps;
    adiReturnItemsModal?: ModalPresentationProps;
    adiReturnItemsBodyContainer?: InjectableCss;
    adiReturnItemsButtonContainer?: GridItemProps;
    addToReturnButtonStyles?: ButtonPresentationProps;
}

export const adiAddSerialNumbersModalStyles: ADIAddSerialNumbersModalStyles = {
    adiTextFieldGridItem: {
        css: css`
            flex-direction: column;
            >${FormFieldStyle}{
                margin-bottom: 10px;
            }
        `
    },
    addToReturnButtonStyles: {
        variant: "secondary",
        css: css`
            display: inline-flex;
            align-items: center;
            font-size: 16px;
            font-weight: bold;
            justify-content: center;
            border-radius: 4px;
            width: 50%;
            height: 40px;
            max-width: 253px;
            border: solid 1px ${getColor("secondary")};
            background: ${getColor("common.background")};
            margin-right: 20px;
            &:focus{
                outline: none;
            }
            &:hover{
                background: ${ADIColors.darkBlue6} !important;
                color: ${ADIColors.blue1} !important;
            }
            @media only screen and (max-width: 576px) {
                width: 100%;
                margin-right: 0px;
                max-width: unset;
            }
        `
    },
    adiReturnItemsButtonContainer: {
        css: css`
            align-items: center; 
            justify-content: center;
        `
    },
    adiReturnItemsBodyContainer: {
        css: css`
        overflow-y: auto;
        min-height: inherit;
        max-height: 62vh;
        padding-right: 18px;
        @media only screen and (max-width: 576px) {
            padding-right: 0px;
        }
        >${TypographyStyle}{
            margin-bottom: 25px;
        }
    `,
    },
    adiReturnItemsModal: {
        cssOverrides: {
            modalContainer: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 20px;
                                height: 85vh !important;
                            `,
                            null,
                            css`
                                height: 75vh;
                            `,
                            null,
                        ],
                        "max",
                    )}
            `,
            modalBody: css`
                border-radius: 6px;
                border: 1px solid ${getColor("common.border")};
                padding: 32px 10px 28px 25px;
                overflow-y: hidden;
                width: 582px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 18px;
                                margin: 0px !important;
                            `,
                            css`
                                height: auto;
                                margin: 45px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
            modalTitle: css`
                border: none;
                padding: 0px;
                margin: 0 0 16px 0;
                > ${TypographyStyle} {
                    font-size: 34px;
                    font-weight: 900;
                    @media only screen and (max-width: 576px) {
                        font-size: 24px;
                    }
                }
                > ${ButtonWrapper} {
                    &:hover {
                        background: transparent;
                        border-color: transparent;
                    }
                    &:focus {
                        outline: none;
                    }
                    > ${IconWrapper} {
                        color: ${getColor("primary")};
                        width: 35px;
                        height: 35px;
                        > svg {
                            width: 35px;
                            height: 35px;
                            stroke-width: 1px;
                        }
                    }
                }
            `,
            modalContent: css`
                padding: 0px;
            `,
        },
    },
};

const styles = adiAddSerialNumbersModalStyles;
export const InputField = (props: any) => {
    const { index, onInputChange, item } = props;
    const [input, setInput] = React.useState("");

    return <TextField
            key={index}
            value={item}
            name={String(index)}
            onChange={(e: any) => { setInput(e.target.value); onInputChange(e); }}
            placeholder={translate("Enter a Serial #")}
    />
}

const ADIAddSerialNumbersModal: FC<Props> = ({ serialNumberData, inputField, isAddSerialNumbersModalOpen, onSerialNumberModalClose, serialNumbersFields, handleSerialNumberChange }) => {
    const [serialData, setSerialData] = React.useState<any>([]);

    React.useEffect(() => {
        setSerialData(serialNumbersFields);
    }, [serialNumbersFields])

    const textUpdateHandler = (e: any) => {
        const newData = [...serialData];
            newData[e.target.name] = e.target.value;
        setSerialData(newData);
    }

    const returnProducts = () => {
        handleSerialNumberChange(serialData);
        onSerialNumberModalClose();
    }

    return (
        <Modal
            {...styles.adiReturnItemsModal}
            headline={translate("Enter Serial Numbers")}
            isOpen={isAddSerialNumbersModalOpen}
            handleClose={() => onSerialNumberModalClose()}
        >
            <GridContainer {...styles.adiReturnItemsBodyContainer}>
                <GridItem width={12} {...styles.adiTextFieldGridItem}>
                    <Typography>{translate("Enter Serial Numbers for the Items in your Return")}</Typography>
                    {serialData && serialData.map((item: any, index: number) => {
                        return <InputField index={index} onInputChange={textUpdateHandler} item={item} />
                    }

                    )}
                </GridItem>
                <GridItem width={12} {...styles.adiReturnItemsButtonContainer}>
                    <Button {...styles.addToReturnButtonStyles}
                    onClick={returnProducts}
                    >{translate("Add to Return")}</Button>
                </GridItem>
            </GridContainer>
        </Modal>
    )
};

export default ADIAddSerialNumbersModal;
